import { EventEmitter } from 'events';
import { performance } from 'perf_hooks';
import type { TaskReporterCompleteTaskStatus, TaskReporterTaskStatus } from './types/TaskReporterTaskStatus.js';

export interface TaskReporterTaskResult {
  status: TaskReporterCompleteTaskStatus;
  /** Main message for the task. */
  message?: string;
  errors?: string[];
  warnings?: string[];
  /** Extra details, shown with verbose or debug logging. */
  details?: string;
  /** Even more information (beyond `details`), only shown with debug logging. */
  extended?: string;
  /** Log this task's completion even if `TaskReporterOptions.showCompleted` is false. */
  forceShow?: boolean;
}

/** Mapping from custom event name to args */
interface TaskEvents {
  start: [];
  complete: [TaskReporterTaskResult];
}

export interface CompletedTaskReporterTask {
  name: string;
  /** Task duration in milliseconds, or undefined if it was canceled or never started */
  duration: number | undefined;
  endedAt: Date;
  result: TaskReporterTaskResult;
}

/**
 * Task recorded and reported by a TaskReporter.
 * Use `reporter.addTask()` to create a new task.
 */
export class TaskReporterTask extends EventEmitter {
  readonly name: string;

  private _result?: TaskReporterTaskResult;
  private _timeStarted?: number;
  private _timeEnded?: number;
  private _endedAt?: Date;

  /**
   * For TaskReporter use only. Use `reporter.addTask()` to create a new task.
   * @internal
   */
  constructor(name: string) {
    super();
    this.name = name;
  }

  /**
   * Current status:
   * - `"pending"` if not started yet
   * - `"running"` if started but not completed
   * - result status if completed
   */
  get status(): TaskReporterTaskStatus {
    return this._result?.status || (this._timeStarted ? 'running' : 'pending');
  }

  /** Get the task duration in ms, or undefined if not finished yet */
  get duration(): number | undefined {
    return this._timeStarted && this._timeEnded ? this._timeEnded - this._timeStarted : undefined;
  }

  /** End date, only if completed */
  get endedAt(): Date | undefined {
    return this._endedAt;
  }

  /** Result, only if completed */
  get result(): Readonly<TaskReporterTaskResult> | undefined {
    return this._result;
  }

  /** Start the task and emit the `'start'` event to the TaskReporter. */
  start(): void {
    this._timeStarted = performance.now();

    this._emit('start');
  }

  /**
   * Complete the task. No-op if called multiple times.
   * @param result - Task result. If `result.status` is unset, defaults to `'complete'` (success).
   */
  complete(result?: Partial<TaskReporterTaskResult>): void {
    if (this._result) {
      return;
    }

    const { status = 'complete', message } = result || {};

    this._timeEnded = performance.now();
    this._endedAt = new Date();
    this._result = { ...result, status, message };

    this._emit('complete', this._result);
  }

  toCompletedJson(): CompletedTaskReporterTask {
    const duration = this.duration;
    const endedAt = this.endedAt;
    if (!this._result || !endedAt) {
      throw new Error('Task is not completed yet');
    }

    return {
      name: this.name,
      result: this._result,
      duration: duration,
      endedAt: endedAt,
    };
  }

  /** Used by TaskReporter to listen for events */
  on<K extends keyof TaskEvents>(event: K, listener: (...args: TaskEvents[K]) => void): this {
    return super.on(event, listener);
  }

  /** For internal use only */
  private _emit<K extends keyof TaskEvents>(event: K, ...args: TaskEvents[K]): boolean {
    return super.emit(event, ...args);
  }
}
